/**
 * Light, dark, or whatever the machine says — the same cookie the site and the
 * panel read, so an operator's choice follows them across all three.
 */

export type Theme = "light" | "dark" | "system";

export const THEME_COOKIE = "ONDIT_THEME";

export function readTheme(): Theme {
	if (typeof document === "undefined") return "system";
	const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${THEME_COOKIE}=([^;]+)`));
	const raw = match ? decodeURIComponent(match[1]) : "";
	return raw === "light" || raw === "dark" ? raw : "system";
}

/**
 * Stamp the resolved theme on the root element.
 *
 * Two marks, because two systems read it: `data-theme` is the console's own
 * stylesheet, and the `dark`/`light` class is what `@ondit/ui`'s tokens key
 * off — without it every library component (inputs, menus, dialogs) draws its
 * light palette on the console's dark chrome.
 */
export function applyTheme(theme: Theme): void {
	const root = document.documentElement;
	if (theme === "system") root.removeAttribute("data-theme");
	else root.setAttribute("data-theme", theme);
	const dark = theme === "dark" || (theme === "system" && matchMedia("(prefers-color-scheme: dark)").matches);
	root.classList.toggle("dark", dark);
	root.classList.toggle("light", !dark);
}

export function writeTheme(theme: Theme): void {
	const host = window.location.hostname;
	const domain = host.endsWith(".ondit.ai") || host === "ondit.ai" ? "; domain=.ondit.ai" : "";
	const secure = window.location.protocol === "https:" ? "; Secure" : "";
	// biome-ignore lint/suspicious/noDocumentCookie: parent-domain cookie, shared with the directory site
	document.cookie = `${THEME_COOKIE}=${theme}; path=/; max-age=31536000; SameSite=Lax${domain}${secure}`;
	applyTheme(theme);
}
